import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
	type AnyCol,
	PdfExportDialog,
	type PdfExportDialogState,
	type PdfMeta,
	buildPdfShowAllInfo,
	exportArchiveGridToExcel,
	headerOf,
} from "@/shared/data-grid";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import {
	CircularProgress,
	ListItemIcon,
	ListItemText,
	MenuItem,
} from "@mui/material";
import { type GridColDef, useGridApiRef } from "@mui/x-data-grid";

type AnyRow = Record<string, unknown>;
type PanelName = "columns" | "filters";

const hasOpenPreferencePanel = (state: unknown, panel: PanelName): boolean => {
	if (!state || typeof state !== "object") return false;

	const preferencePanel = (state as { preferencePanel?: unknown })
		.preferencePanel;
	if (!preferencePanel || typeof preferencePanel !== "object") return false;

	const panelRecord = preferencePanel as Record<string, unknown>;
	return panelRecord.open === true && panelRecord.openedPanelValue === panel;
};

const isHiddenColumn = (col: GridColDef): boolean => {
	return (col as { hide?: boolean }).hide === true;
};

type UseShiftTableHeaderActionsArgs = {
	setHeaderActions: (node: React.ReactNode) => void;
	isMobile: boolean;
	exportableRows: AnyRow[];
	exportColumns: GridColDef[];
	exportFileName: string;
	pdfMeta?: PdfMeta;
	leadingActions?: React.ReactNode;
};

export function useShiftTableHeaderActions({
	setHeaderActions,
	isMobile,
	exportableRows,
	exportColumns,
	exportFileName,
	pdfMeta,
	leadingActions,
}: UseShiftTableHeaderActionsArgs) {
	const apiRef = useGridApiRef();
	const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
	const [isPdfExporting, setIsPdfExporting] = useState(false);
	const [isExcelExporting, setIsExcelExporting] = useState(false);
	const [selectedPdfFields, setSelectedPdfFields] = useState<Set<string>>(
		() => new Set(),
	);
	const [pdfExportOpts, setPdfExportOpts] = useState<PdfExportDialogState>({
		dateFormat: pdfMeta?.dateFormat ?? "DD/MM/YY HH:mm:ss",
		footerText: pdfMeta?.footerText ?? "Satos",
		truncateAt: pdfMeta?.truncateAt ?? 200,
		truncateEnabled: false,
		pageSize: pdfMeta?.pageSize ?? "a4",
		groupBy: "",
	});

	const pdfColumns = useMemo<AnyCol[]>(
		() =>
			exportColumns
				.filter((col) => Boolean(col.field) && !isHiddenColumn(col))
				.map((col) => ({
					field: col.field,
					headerName: col.headerName,
					minWidth: col.minWidth,
				})),
		[exportColumns],
	);

	const handleOpenColumnsPanel = useCallback(() => {
		if (isMobile) return;

		const api = apiRef.current;
		if (!api) return;

		if (hasOpenPreferencePanel(api.state, "columns")) {
			api.hidePreferences?.();
			return;
		}

		api.showPreferences?.("columns");
	}, [apiRef, isMobile]);

	const handleOpenFilterPanel = useCallback(() => {
		if (isMobile) return;

		const api = apiRef.current;
		if (!api) return;

		if (hasOpenPreferencePanel(api.state, "filters")) {
			api.hidePreferences?.();
			return;
		}

		api.showPreferences?.("filters");
	}, [apiRef, isMobile]);

	const handleExportCsv = useCallback(() => {
		apiRef.current?.exportDataAsCsv?.({
			fileName: exportFileName,
			utf8WithBom: true,
			delimiter: ",",
			includeHeaders: true,
		});
	}, [apiRef, exportFileName]);

	const handleExportExcel = useCallback(async () => {
		if (isExcelExporting) return;
		setIsExcelExporting(true);
		try {
			await exportArchiveGridToExcel(apiRef, { fileName: exportFileName });
		} finally {
			setIsExcelExporting(false);
		}
	}, [apiRef, exportFileName, isExcelExporting]);

	const openPdfExportDialog = useCallback(() => {
		setSelectedPdfFields(new Set(pdfColumns.map((col) => col.field)));
		setPdfExportOpts((prev) => ({
			...prev,
			dateFormat: pdfMeta?.dateFormat ?? prev.dateFormat,
			footerText: pdfMeta?.footerText ?? prev.footerText,
			truncateAt: pdfMeta?.truncateAt ?? prev.truncateAt,
			pageSize: pdfMeta?.pageSize ?? prev.pageSize,
		}));
		setPdfDialogOpen(true);
	}, [pdfColumns, pdfMeta]);

	const togglePdfColumn = useCallback((field: string) => {
		setSelectedPdfFields((prev) => {
			const next = new Set(prev);
			if (next.has(field)) next.delete(field);
			else next.add(field);
			return next;
		});
	}, []);

	const selectAllPdfColumns = useCallback(() => {
		setSelectedPdfFields(new Set(pdfColumns.map((col) => col.field)));
	}, [pdfColumns]);

	const deselectAllPdfColumns = useCallback(() => {
		setSelectedPdfFields(new Set());
	}, []);

	const handlePdfExport = useCallback(async () => {
		const selectedCols = pdfColumns.filter((col) =>
			selectedPdfFields.has(col.field),
		);
		if (selectedCols.length === 0) return;

		setIsPdfExporting(true);
		try {
			await buildPdfShowAllInfo(
				exportableRows,
				pdfColumns,
				exportFileName,
				pdfMeta,
				{
					selectedColumns: selectedCols,
					dateFormat: pdfExportOpts.dateFormat,
					footerText: pdfExportOpts.footerText || "Satos",
					truncateAt: pdfExportOpts.truncateEnabled
						? pdfExportOpts.truncateAt || 200
						: undefined,
					pageSize: pdfExportOpts.pageSize,
					groupBy: pdfExportOpts.groupBy || undefined,
				},
			);
			setPdfDialogOpen(false);
		} catch (e) {
			console.error("PDF export failed:", e);
		} finally {
			setIsPdfExporting(false);
		}
	}, [
		exportColumns,
		exportFileName,
		exportableRows,
		pdfColumns,
		pdfExportOpts,
		pdfMeta,
		selectedPdfFields,
	]);

	useEffect(() => {
		const actions = (
			<>
				{leadingActions}
				{!isMobile && (
					<MenuItem onClick={handleOpenColumnsPanel}>
						<ListItemIcon>
							<ViewWeekRoundedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Columns" />
					</MenuItem>
				)}
				{!isMobile && (
					<MenuItem onClick={handleOpenFilterPanel}>
						<ListItemIcon>
							<FilterAltRoundedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Filters" />
					</MenuItem>
				)}
				<MenuItem
					onClick={handleExportCsv}
					disabled={isMobile || exportableRows.length === 0}
				>
					<ListItemIcon>
						<TableViewOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Export CSV" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						void handleExportExcel();
					}}
					disabled={isMobile || exportableRows.length === 0 || isExcelExporting}
				>
					<ListItemIcon>
						{isExcelExporting ? (
							<CircularProgress size={16} />
						) : (
							<TableChartIcon fontSize="small" />
						)}
					</ListItemIcon>
					<ListItemText
						primary={isExcelExporting ? "Exporting Excel..." : "Export Excel"}
					/>
				</MenuItem>
				<MenuItem
					onClick={openPdfExportDialog}
					disabled={exportableRows.length === 0}
				>
					<ListItemIcon>
						<PictureAsPdfOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Export PDF" />
				</MenuItem>
			</>
		);

		setHeaderActions(actions);
		return () => setHeaderActions(null);
	}, [
		exportableRows,
		handleExportCsv,
		handleExportExcel,
		handleOpenColumnsPanel,
		handleOpenFilterPanel,
		isExcelExporting,
		isMobile,
		leadingActions,
		openPdfExportDialog,
		setHeaderActions,
	]);

	const pdfDialog = (
		<PdfExportDialog
			open={pdfDialogOpen}
			onClose={() => !isPdfExporting && setPdfDialogOpen(false)}
			isExporting={isPdfExporting}
			cols={pdfColumns}
			headerOf={headerOf}
			selectedFields={selectedPdfFields}
			toggleColumn={togglePdfColumn}
			selectAllColumns={selectAllPdfColumns}
			deselectAllColumns={deselectAllPdfColumns}
			exportOpts={pdfExportOpts}
			setExportOpts={setPdfExportOpts}
			onExport={() => {
				void handlePdfExport();
			}}
			canExport={selectedPdfFields.size > 0}
		/>
	);

	const toolbarProps = useMemo(
		() => ({
			hidden: true,
			rows: exportableRows,
			columns: exportColumns,
			fileName: exportFileName,
			hideQuickFilter: true,
			showPdfOptions: true,
			pdfMeta,
		}),
		[exportColumns, exportFileName, exportableRows, pdfMeta],
	);

	return {
		apiRef,
		pdfDialog,
		toolbarProps,
	};
}
